// src/hooks/useTelegramWebApp.ts
import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import type { WebApp } from '@twa-dev/types';

// ✅ Расширяем глобальный интерфейс Window
declare global {
  interface Window {
    Telegram?: {
      WebApp: WebApp;
    };
  }
}

// ✅ УСОВЕРШЕНСТВОВАННЫЕ mock данные для разработки
const ENHANCED_MOCK_USER: any = {
  id: 987654321,
  first_name: null,
  last_name: null,
  username: null,
  language_code: 'ru',
  is_premium: false,
  photo_url: undefined,
};

const createAdvancedMockWebApp = (): WebApp => {
  const mockWebApp: Partial<WebApp> = {
    version: '8.0',
    platform: 'tdesktop',
    colorScheme: 'light',
    isExpanded: true,
    viewportHeight: 844,
    viewportStableHeight: 844,
    headerColor: '#7B68EE',
    backgroundColor: '#ffffff',
    isClosingConfirmationEnabled: false,
    initData: 'mock_init_data_2025', // ⬅️ моковая строка (без hash=)
    initDataUnsafe: {
      user: ENHANCED_MOCK_USER,
      auth_date: Math.floor(Date.now() / 1000),
      hash: 'mock_hash_123456', // у мока не должен считаться «реальным»
      signature: '',
    } as any,
    themeParams: {
      bg_color: '#ffffff',
      text_color: '#000000',
      hint_color: '#999999',
      link_color: '#7B68EE',
      button_color: '#7B68EE',
      button_text_color: '#ffffff',
      secondary_bg_color: '#f1f1f1',
    } as any,

    ready: () => {
      if (process.env.NODE_ENV === 'development') {
        console.log('📱 Mock WebApp ready (2025)');
      }
    },
    expand: () => {
      if (process.env.NODE_ENV === 'development') {
        console.log('📱 Mock WebApp expand');
      }
    },
    close: () => {
      if (process.env.NODE_ENV === 'development') {
        console.log('📱 Mock WebApp close');
      }
      if (window.history.length > 1) window.history.back();
    },

    MainButton: {
      text: '',
      color: '#7B68EE',
      textColor: '#ffffff',
      isVisible: false,
      isProgressVisible: false,
      isActive: true,
      setText: (text: string) => {
        if (process.env.NODE_ENV === 'development') {
          console.log('📱 MainButton setText:', text);
        }
      },
      onClick: (fn: () => void) => {
        if (process.env.NODE_ENV === 'development') {
          console.log('📱 MainButton onClick установлен');
        }
        (window as any).__mockMainButtonCallback = fn;
      },
      offClick: (_fn: () => void) => {
        if (process.env.NODE_ENV === 'development') {
          console.log('📱 MainButton offClick');
        }
      },
      show: () => {
        if (process.env.NODE_ENV === 'development') {
          console.log('📱 MainButton show');
        }
      },
      hide: () => {
        if (process.env.NODE_ENV === 'development') {
          console.log('📱 MainButton hide');
        }
      },
      enable: () => {
        if (process.env.NODE_ENV === 'development') {
          console.log('📱 MainButton enable');
        }
      },
      disable: () => {
        if (process.env.NODE_ENV === 'development') {
          console.log('📱 MainButton disable');
        }
      },
      showProgress: (_leaveActive?: boolean) => {
        if (process.env.NODE_ENV === 'development') {
          console.log('📱 MainButton showProgress');
        }
      },
      hideProgress: () => {
        if (process.env.NODE_ENV === 'development') {
          console.log('📱 MainButton hideProgress');
        }
      },
      setParams: (params: any) => {
        if (process.env.NODE_ENV === 'development') {
          console.log('📱 MainButton setParams:', params);
        }
      },
    } as any,

    BackButton: {
      isVisible: false,
      onClick: (fn: () => void) => {
        if (process.env.NODE_ENV === 'development') {
          console.log('📱 BackButton onClick установлен');
        }
        (window as any).__mockBackButtonCallback = fn;
      },
      offClick: (_fn: () => void) => {
        if (process.env.NODE_ENV === 'development') {
          console.log('📱 BackButton offClick');
        }
      },
      show: () => {
        if (process.env.NODE_ENV === 'development') {
          console.log('📱 BackButton show');
        }
      },
      hide: () => {
        if (process.env.NODE_ENV === 'development') {
          console.log('📱 BackButton hide');
        }
      },
    } as any,

    HapticFeedback: {
      impactOccurred: (style: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft') => {
        if (process.env.NODE_ENV === 'development') {
          console.log('📱 HapticFeedback impact:', style);
        }
        if (navigator.vibrate) {
          const patterns: Record<string, number[]> = {
            light:[25],
            medium:[50],
            heavy:[100],
            rigid:[75],
            soft:[30],
          };
          navigator.vibrate(patterns[style] ||[25]);
        }
        if (process.env.NODE_ENV === 'development') {
          (document.body as HTMLElement).style.transform = 'scale(0.98)';
          setTimeout(() => ((document.body as HTMLElement).style.transform = 'scale(1)'), 100);
        }
      },
      notificationOccurred: (type: 'error' | 'success' | 'warning') => {
        if (process.env.NODE_ENV === 'development') {
          console.log('📱 HapticFeedback notification:', type);
        }
        if (navigator.vibrate) {
          const patterns: Record<string, number[]> = {
            error:[100, 50, 100],
            success:[50, 25, 50],
            warning:[75, 50, 75],
          };
          navigator.vibrate(patterns[type]);
        }
      },
      selectionChanged: () => {
        if (process.env.NODE_ENV === 'development') {
          console.log('📱 HapticFeedback selection');
        }
        if (navigator.vibrate) navigator.vibrate([10]);
      },
    } as any,

    setHeaderColor: (color: string) => {
      if (process.env.NODE_ENV === 'development') {
        console.log('📱 setHeaderColor:', color);
      }
      document.documentElement.style.setProperty('--tg-theme-header-color', color);
    },
    setBackgroundColor: (color: string) => {
      if (process.env.NODE_ENV === 'development') {
        console.log('📱 setBackgroundColor:', color);
      }
      document.documentElement.style.setProperty('--tg-theme-bg-color', color);
    },
    enableClosingConfirmation: () => {
      if (process.env.NODE_ENV === 'development') {
        console.log('📱 enableClosingConfirmation');
      }
    },
    disableClosingConfirmation: () => {
      if (process.env.NODE_ENV === 'development') {
        console.log('📱 disableClosingConfirmation');
      }
    },
    openTelegramLink: (url: string) => {
      if (process.env.NODE_ENV === 'development') {
        console.log('📱 Mock openTelegramLink:', url);
      }
      // В dev режиме просто открываем в новом окне
      window.open(url, '_blank');
    },

    showAlert: (message: string, callback?: () => void) => {
      if (process.env.NODE_ENV === 'development') {
        console.log('📱 showAlert:', message);
      }
      alert(message);
      callback?.();
    },
    showConfirm: (message: string, callback?: (confirmed: boolean) => void) => {
      if (process.env.NODE_ENV === 'development') {
        console.log('📱 showConfirm:', message);
      }
      const confirmResult = window.confirm(message);
      callback?.(confirmResult);
    },
    showPopup: (params: any, callback?: (buttonId?: string) => void) => {
      if (process.env.NODE_ENV === 'development') {
        console.log('📱 showPopup:', params);
      }
      if (params.buttons && params.buttons.length > 1) {
        const confirmResult = window.confirm(`${params.title || ''}\n${params.message}`);
        callback?.(confirmResult ? params.buttons[1].id : params.buttons[0].id);
      } else {
        alert(`${params.title || ''}\n${params.message}`);
        callback?.(params.buttons?.[0]?.id);
      }
    },

    CloudStorage: {
      setItem: (key: string, value: string, cb?: (error: Error | null) => void) => {
        if (process.env.NODE_ENV === 'development') {
          console.log('📱 Mock CloudStorage.setItem:', key);
        }
        try { localStorage.setItem(key, value); cb?.(null); } catch (e) { cb?.(e as Error); }
      },
      getItem: (key: string, cb: (error: Error | null, value?: string) => void) => {
        if (process.env.NODE_ENV === 'development') {
          console.log('📱 Mock CloudStorage.getItem:', key);
        }
        try { cb(null, localStorage.getItem(key) || undefined); } catch (e) { cb(e as Error); }
      },
      getItems: (keys: string[], cb: (error: Error | null, values?: Record<string, string>) => void) => {
        if (process.env.NODE_ENV === 'development') {
          console.log('📱 Mock CloudStorage.getItems:', keys);
        }
        try {
          const res: Record<string, string> = {};
          keys.forEach(k => { const v = localStorage.getItem(k); if (v) res[k] = v; });
          cb(null, res);
        } catch (e) { cb(e as Error); }
      },
      removeItem: (key: string, cb?: (error: Error | null) => void) => {
        if (process.env.NODE_ENV === 'development') {
          console.log('📱 Mock CloudStorage.removeItem:', key);
        }
        try { localStorage.removeItem(key); cb?.(null); } catch (e) { cb?.(e as Error); }
      },
      removeItems: (keys: string[], cb?: (error: Error | null) => void) => {
        if (process.env.NODE_ENV === 'development') {
          console.log('📱 Mock CloudStorage.removeItems:', keys);
        }
        try { keys.forEach(k => localStorage.removeItem(k)); cb?.(null); } catch (e) { cb?.(e as Error); }
      },
      getKeys: (cb: (error: Error | null, keys?: string[]) => void) => {
        if (process.env.NODE_ENV === 'development') {
          console.log('📱 Mock CloudStorage.getKeys');
        }
        try { cb(null, Object.keys(localStorage)); } catch (e) { cb(e as Error); }
      },
    } as any,
  };

  return mockWebApp as WebApp;
};

// ✅ КРИТИЧНО: Глобальный флаг инициализации вместо useRef
// useRef создаётся в каждом компоненте отдельно, что НЕ защищает от множественной инициализации!
// Этот хук используется в 13+ компонентах (App, AchievementToast, CalendarPage, и т.д.)
let globalIsInitialized = false;
let cachedWebApp: WebApp | null = null;
let cachedUser: any = null;
let cachedTheme: 'light' | 'dark' = 'light';
let cachedIsRealTelegram = false;

export const useTelegramWebApp = () => {
  const[user, setUser] = useState<any>(null);
  const[webApp, setWebApp] = useState<WebApp | null>(null);
  const[isReady, setIsReady] = useState(false);
  const[theme, setTheme] = useState<'light' | 'dark'>('light');
  const[isRealTelegram, setIsRealTelegram] = useState(false); // ⬅️ добавлено

  // ✅ Ref для хранения текущих обработчиков (защита от утечек)
  const mainButtonHandlerRef = useRef<(() => void) | null>(null);
  const backButtonHandlerRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    if (globalIsInitialized) {
      setWebApp(cachedWebApp);
      setUser(cachedUser);
      setTheme(cachedTheme);
      document.documentElement.setAttribute('data-theme', cachedTheme);
      setIsRealTelegram(cachedIsRealTelegram);
      setIsReady(true);
      return;
    }
    if (process.env.NODE_ENV === 'development') {
      console.log('🚀 Инициализация Telegram WebApp 2025...');
    }
    globalIsInitialized = true;

    let currentWebApp: WebApp;
    let currentUser: any;

    if (window.Telegram?.WebApp) {
      if (process.env.NODE_ENV === 'development') {
        console.log('✅ Найден реальный Telegram WebApp');
      }
      currentWebApp = window.Telegram.WebApp;

      try {
        currentWebApp.ready();
        currentWebApp.expand();
        currentWebApp.setHeaderColor?.('#7B68EE');
        currentWebApp.setBackgroundColor?.('#ffffff');
        currentWebApp.enableClosingConfirmation?.();
        if (process.env.NODE_ENV === 'development') {
          console.log('🎨 WebApp настроен под Geltek');
        }
      } catch (error) {
        console.warn('⚠️ Ошибка настройки WebApp:', error);
      }

      currentUser = currentWebApp.initDataUnsafe?.user || ENHANCED_MOCK_USER;

      // ✅ Устанавливаем тему из Telegram.WebApp.colorScheme
      const telegramTheme = currentWebApp.colorScheme || 'light';
      setTheme(telegramTheme);
      document.documentElement.setAttribute('data-theme', telegramTheme);
      if (process.env.NODE_ENV === 'development') {
        console.log('🎨 Установлена тема из Telegram:', telegramTheme);
      }
    } else {
      if (process.env.NODE_ENV === 'development') {
        console.log('🔧 Telegram WebApp не найден, используем enhanced mock');
      }
      currentWebApp = createAdvancedMockWebApp();
      currentUser = ENHANCED_MOCK_USER;

      // ✅ Fallback на системную тему если нет Telegram
      const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
      setTheme(systemTheme);
      document.documentElement.setAttribute('data-theme', systemTheme);
      if (process.env.NODE_ENV === 'development') {
        console.log('🎨 Установлена системная тема:', systemTheme);
      }
    }

    const applyTheme = (themeParams: any) => {
      Object.entries(themeParams).forEach(([key, value]) => {
        if (value) {
          document.documentElement.style.setProperty(
            `--tg-theme-${(key as string).replace(/_/g, '-')}`,
            value as string
          );
        }
      });
    };

    if ((currentWebApp as any).themeParams) {
      applyTheme((currentWebApp as any).themeParams);
    }

    // ✅ Устанавливаем CSS-переменные для viewport (критично для iOS)
    const updateViewportVariables = () => {
      const vh = currentWebApp.viewportHeight;
      const stableVh = currentWebApp.viewportStableHeight;

      // Устанавливаем CSS-переменные для использования вместо 100vh
      document.documentElement.style.setProperty('--tg-viewport-height', `${vh}px`);
      document.documentElement.style.setProperty('--tg-viewport-stable-height', `${stableVh}px`);

      if (process.env.NODE_ENV === 'development') {
        console.log('📏 Viewport переменные установлены:', { vh, stableVh });
      }
    };

    updateViewportVariables(); // Устанавливаем при инициализации

    // 👉 определяем «реальный» Telegram по initData с hash=
    const initDataStr = String((currentWebApp as any).initData || '');
    const real = Boolean(window.Telegram?.WebApp) && initDataStr.includes('hash=');
    setIsRealTelegram(real);

    // ✅ Подписка на изменение темы
    const handleThemeChanged = () => {
      const newTheme = currentWebApp.colorScheme || 'light';
      if (process.env.NODE_ENV === 'development') {
        console.log('🎨 Тема изменена на:', newTheme);
      }
      setTheme(newTheme);
      document.documentElement.setAttribute('data-theme', newTheme);
      if ((currentWebApp as any).themeParams) {
        applyTheme((currentWebApp as any).themeParams);
      }
    };

    // ✅ Подписка на изменение viewport (особенно важно для iOS keyboard)
    const handleViewportChanged = () => {
      if (process.env.NODE_ENV === 'development') {
        console.log('📐 Viewport изменён:', {
          height: currentWebApp.viewportHeight,
          stableHeight: currentWebApp.viewportStableHeight,
          isExpanded: currentWebApp.isExpanded,
        });
      }

      // ✅ КРИТИЧНО: Обновляем CSS-переменные при изменении viewport (iOS keyboard)
      updateViewportVariables();
    };

    // Подписываемся на события (только для реального Telegram)
    if (window.Telegram?.WebApp && typeof (currentWebApp as any).onEvent === 'function') {
      (currentWebApp as any).onEvent('themeChanged', handleThemeChanged);
      (currentWebApp as any).onEvent('viewportChanged', handleViewportChanged);
      if (process.env.NODE_ENV === 'development') {
        console.log('✅ Подписка на themeChanged и viewportChanged установлена');
      }
    }

    const resolvedTheme =
      (currentWebApp.colorScheme as 'light' | 'dark' | undefined) ||
      ((document.documentElement.getAttribute('data-theme') as 'light' | 'dark' | null) ?? 'light');

    cachedWebApp = currentWebApp;
    cachedUser = currentUser;
    cachedTheme = resolvedTheme;
    cachedIsRealTelegram = real;

    setWebApp(currentWebApp);
    setUser(currentUser);
    setIsReady(true);
    setTheme(resolvedTheme);
    
    if (process.env.NODE_ENV === 'development') {
      console.log('✅ WebApp 2025 инициализирован:', {
        platform: currentWebApp.platform,
        version: currentWebApp.version,
        user: currentUser?.first_name ?? null,
        theme: (currentWebApp as any).colorScheme,
        cloudStorage: !!(currentWebApp as any).CloudStorage,
        isRealTelegram: real,
      });
    }

    return () => {
      if (process.env.NODE_ENV === 'development') {
        console.log('🧹 Cleanup WebApp hook');
      }

      // ✅ Отписываемся от событий при размонтировании
      if (window.Telegram?.WebApp && typeof (currentWebApp as any).offEvent === 'function') {
        (currentWebApp as any).offEvent('themeChanged', handleThemeChanged);
        (currentWebApp as any).offEvent('viewportChanged', handleViewportChanged);
        if (process.env.NODE_ENV === 'development') {
          console.log('✅ Отписка от событий выполнена');
        }
      }

      // ✅ Удаляем обработчики MainButton/BackButton при размонтировании
      if (mainButtonHandlerRef.current && currentWebApp?.MainButton) {
        try {
          (currentWebApp.MainButton as any).offClick(mainButtonHandlerRef.current);
          mainButtonHandlerRef.current = null;
          if (process.env.NODE_ENV === 'development') {
            console.log('✅ MainButton: обработчик удалён при размонтировании');
          }
        } catch (error) {
          console.warn('⚠️ Ошибка удаления MainButton обработчика:', error);
        }
      }

      if (backButtonHandlerRef.current && currentWebApp?.BackButton) {
        try {
          (currentWebApp.BackButton as any).offClick(backButtonHandlerRef.current);
          backButtonHandlerRef.current = null;
          if (process.env.NODE_ENV === 'development') {
            console.log('✅ BackButton: обработчик удалён при размонтировании');
          }
        } catch (error) {
          console.warn('⚠️ Ошибка удаления BackButton обработчика:', error);
        }
      }
    };
  },[]);

  // ✅ Современные методы обратной связи
  const hapticFeedback = useCallback(
    (type: 'light' | 'medium' | 'heavy' | 'success' | 'error' | 'warning') => {
      if (!webApp?.HapticFeedback) return;
      switch (type) {
        case 'light':
        case 'medium':
        case 'heavy':
          webApp.HapticFeedback.impactOccurred(type as 'light' | 'medium' | 'heavy');
          break;
        case 'success':
        case 'error':
        case 'warning':
          webApp.HapticFeedback.notificationOccurred(type as 'success' | 'error' | 'warning');
          break;
      }
    },
[webApp]
  );

  // ✅ Управление MainButton (с защитой от утечек)
  const showMainButton = useCallback(
    (text: string, onClick: () => void) => {
      if (!webApp?.MainButton) return;
      try {
        // ✅ Удаляем предыдущий обработчик если он есть
        if (mainButtonHandlerRef.current) {
          (webApp.MainButton as any).offClick(mainButtonHandlerRef.current);
          if (process.env.NODE_ENV === 'development') {
            console.log('📱 MainButton: предыдущий обработчик удалён');
          }
        }

        // Сохраняем новый обработчик в ref
        mainButtonHandlerRef.current = onClick;

        // Устанавливаем новый обработчик
        (webApp.MainButton as any).setText(text);
        (webApp.MainButton as any).onClick(onClick);
        (webApp.MainButton as any).show();
        (webApp.MainButton as any).enable();
        if (process.env.NODE_ENV === 'development') {
          console.log('📱 MainButton показана:', text);
        }
      } catch (error) {
        console.warn('⚠️ Ошибка показа MainButton:', error);
      }
    },
[webApp]
  );

  const hideMainButton = useCallback(() => {
    if (!webApp?.MainButton) return;
    try {
      // ✅ Удаляем обработчик при скрытии
      if (mainButtonHandlerRef.current) {
        (webApp.MainButton as any).offClick(mainButtonHandlerRef.current);
        mainButtonHandlerRef.current = null;
        if (process.env.NODE_ENV === 'development') {
          console.log('📱 MainButton: обработчик удалён при скрытии');
        }
      }

      (webApp.MainButton as any).hide();
      if (process.env.NODE_ENV === 'development') {
        console.log('📱 MainButton скрыта');
      }
    } catch (error) {
      console.warn('⚠️ Ошибка скрытия MainButton:', error);
    }
  },[webApp]);

  // ✅ Управление BackButton (с защитой от утечек)
  const showBackButton = useCallback(
    (onClick: () => void) => {
      if (!webApp?.BackButton) return;
      try {
        // ✅ Удаляем предыдущий обработчик если он есть
        if (backButtonHandlerRef.current) {
          (webApp.BackButton as any).offClick(backButtonHandlerRef.current);
          if (process.env.NODE_ENV === 'development') {
            console.log('📱 BackButton: предыдущий обработчик удалён');
          }
        }

        // Сохраняем новый обработчик в ref
        backButtonHandlerRef.current = onClick;

        // Устанавливаем новый обработчик
        (webApp.BackButton as any).onClick(onClick);
        (webApp.BackButton as any).show();
        if (process.env.NODE_ENV === 'development') {
          console.log('📱 BackButton показана');
        }
      } catch (error) {
        console.warn('⚠️ Ошибка показа BackButton:', error);
      }
    },
[webApp]
  );

  const hideBackButton = useCallback(() => {
    if (!webApp?.BackButton) return;
    try {
      // ✅ Удаляем обработчик при скрытии
      if (backButtonHandlerRef.current) {
        (webApp.BackButton as any).offClick(backButtonHandlerRef.current);
        backButtonHandlerRef.current = null;
        if (process.env.NODE_ENV === 'development') {
          console.log('📱 BackButton: обработчик удалён при скрытии');
        }
      }

      (webApp.BackButton as any).hide();
      if (process.env.NODE_ENV === 'development') {
        console.log('📱 BackButton скрыта');
      }
    } catch (error) {
      console.warn('⚠️ Ошибка скрытия BackButton:', error);
    }
  },[webApp]);

  // ✅ Диалоги
  const showAlert = useCallback(
    (message: string): Promise<void> =>
      new Promise((resolve) => {
        if ((webApp as any)?.showAlert) {
          (webApp as any).showAlert(message, () => resolve());
        } else {
          alert(message);
          resolve();
        }
      }),
[webApp]
  );

  const showConfirm = useCallback(
    (message: string): Promise<boolean> =>
      new Promise((resolve) => {
        if ( (webApp as any)?.showConfirm ) {
          (webApp as any).showConfirm(message, (confirmed: boolean) => resolve(confirmed));
        } else {
          resolve(window.confirm(message));
        }
      }),
[webApp]
  );

  const close = useCallback(() => {
    if ((webApp as any)?.close) (webApp as any).close();
    else window.close();
  },[webApp]);

  const expand = useCallback(() => {
    if ((webApp as any)?.expand) (webApp as any).expand();
  },[webApp]);

  // ✅ Стабильный возврат значений (+ isRealTelegram)
  const returnValue = useMemo(
    () => ({
      user,
      webApp,
      isReady,
      theme,
      isRealTelegram, // ⬅️ экспортим
      hapticFeedback,
      showMainButton,
      hideMainButton,
      showBackButton,
      hideBackButton,
      close,
      expand,
      showAlert,
      showConfirm,
    }),
[
      user,
      webApp,
      isReady,
      theme,
      isRealTelegram,
      hapticFeedback,
      showMainButton,
      hideMainButton,
      showBackButton,
      hideBackButton,
      close,
      expand,
      showAlert,
      showConfirm,
    ]
  );

  return returnValue;
};
